import { Redirect } from 'react-router-dom';

import { CheckIfUserHasPortalPrivilege, PORTAL_PRIVILEGE } from '../common/client-status/interfaces';

import {
  AccountTransfers,
  AirtimeAndData,
  CustomerAuthentication,
  CustomerEnrollment,
  Dashboard,
  Kyc,
  MerchantPayment,
  Ondemand,
  OperatorLookup,
  Payout,
  ProductsAndServices,
  Sms,
  SubAccountsManagement,
  Ussd,
  UssdEditor,
  Wallet
} from '../core/portal/pages';

import { PAGE_TITLE } from '../core/portal/consts';
import { PORTAL_PATH } from './paths';
import { CommonRoute, PortalRoute } from './interfaces';
import Wap from '../core/portal/pages/wap';
import Voice from '../core/portal/pages/voice';

// Redirection

function redirectTo(portalPath: PORTAL_PATH): CommonRoute {
  return {
    render: () => <Redirect to={portalPath} />,
    isRedirect: true
  };
}

// No dedicated section
const dashboardRoute: PortalRoute = {
  path: PORTAL_PATH.DASHBOARD,
  render: () => <Dashboard />,
  title: PAGE_TITLE.DASHBOARD,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) => verify('OPERATION_HISTORY')
};

const walletRoute: PortalRoute = {
  path: PORTAL_PATH.WALLET,
  render: () => <Wallet />,
  title: PAGE_TITLE.WALLET,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) => verify('WALLET_CREDIT')
};

const accountManagementRoute: PortalRoute = {
  path: PORTAL_PATH.SUB_ACCOUNTS_MANAGEMENT,
  render: () => <SubAccountsManagement />,
  title: PAGE_TITLE.USER_MANAGEMENT,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) => verify('USER_MANAGEMENT')
};

// Access
const ussdRoute: PortalRoute = {
  path: PORTAL_PATH.USSD,
  render: () => <Ussd />,
  title: PAGE_TITLE.USSD,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) =>
    verify('USSD_BILLING_REPORTS') || verify('USSD_SETTINGS')
};
const wapRoute: PortalRoute = {
  path: PORTAL_PATH.WAP,
  render: () => <Wap />,
  title: PAGE_TITLE.WAP,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) =>
    verify('WAP_BILLING_REPORTS') || verify('WAP_SETTINGS')
};
const voiceRoute: PortalRoute = {
  path: PORTAL_PATH.VOICE,
  render: () => <Voice />,
  title: PAGE_TITLE.VOICE,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) =>
    verify('VOICE_BILLING_REPORTS') || verify('VOICE_SETTINGS')
};
const ussdEditorRoute: PortalRoute = {
  path: PORTAL_PATH.USSD_EDITOR,
  render: () => <UssdEditor />,
  title: PAGE_TITLE.USSD_EDITOR,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) => verify('USSD_SETTINGS')
};
const smsRoute: PortalRoute = {
  path: PORTAL_PATH.SMS,
  render: () => <Sms />,
  title: PAGE_TITLE.SMS,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) => verify('SMS_BILLING_REPORTS')
};
const operatorLookupRoute: PortalRoute = {
  path: PORTAL_PATH.OPERATOR_LOOKUP,
  render: () => <OperatorLookup />,
  title: PAGE_TITLE.OPERATOR_LOOKUP,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) => verify('OPERATOR_LOOKUP_BILLING_REPORTS')
};

// Value
const airtimeAndDataRoute: PortalRoute = {
  path: PORTAL_PATH.AIRTIME_AND_DATA,
  render: () => <AirtimeAndData />,
  title: PAGE_TITLE.AIRTIME_AND_DATA,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) => verify('AIRTIME_AND_DATA_BILLING_REPORTS')
};
const accountTransfersRoute: PortalRoute = {
  path: PORTAL_PATH.ACCOUNT_TRANSFERS,
  render: () => <AccountTransfers />,
  title: PAGE_TITLE.ACCOUNT_TRANSFERS,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) => verify('TRANSFER_BILLING_REPORTS')
};
const ondemandRoute: PortalRoute = {
  path: PORTAL_PATH.ONDEMAND,
  render: () => <Ondemand />,
  title: PAGE_TITLE.ONDEMAND,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) =>
    verify('ON_DEMAND_PAYMENT_AND_ACQUISITION_BILLING_REPORTS')
};
const payoutRoute: PortalRoute = {
  path: PORTAL_PATH.PAYOUT,
  render: () => <Payout />,
  title: PAGE_TITLE.PAYOUT,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) => verify('PAYOUT_BILLING_REPORTS')
};
const merchantPaymentRoute: PortalRoute = {
  path: PORTAL_PATH.MERCHANT_PAYMENT,
  render: () => <MerchantPayment />,
  title: PAGE_TITLE.MERCHANT_PAYMENT,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) => verify('MERCHANT_PAYMENT_BILLING_REPORTS')
};

// Identity
const customerValidationRoute: PortalRoute = {
  path: PORTAL_PATH.CUSTOMER_VALIDATION,
  render: () => <Kyc />,
  title: PAGE_TITLE.CUSTOMER_VALIDATION,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) => verify('CUSTOMER_VALIDATION_BILLING_REPORTS')
};
const customerAuthenticationRoute: PortalRoute = {
  path: PORTAL_PATH.CUSTOMER_AUTHENTICATION,
  render: () => <CustomerAuthentication />,
  title: PAGE_TITLE.CUSTOMER_AUTHENTICATION,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) =>
    verify('CUSTOMER_AUTHENTICATION_BILLING_REPORTS')
};
const customerEnrollmentRoute: PortalRoute = {
  path: PORTAL_PATH.CUSTOMER_ENROLLMENT,
  render: () => <CustomerEnrollment />,
  title: PAGE_TITLE.CUSTOMER_ENROLLMENT,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) => verify('CUSTOMER_ENROLLMENT_BILLING_REPORTS')
};

// Products and services
const productsAndServicesRoute: PortalRoute = {
  path: PORTAL_PATH.PRODUCTS_AND_SERVICES,
  render: () => <ProductsAndServices />,
  title: PAGE_TITLE.PRODUCTS_AND_SERVICES,
  portalPrivilegeVerification: (verify: CheckIfUserHasPortalPrivilege) => verify('EXPLORE_PRODUCTS')
};

const portalRoutes: PortalRoute[] = [
  dashboardRoute,
  walletRoute,
  ussdRoute,
  ussdEditorRoute,
  wapRoute,
  voiceRoute,
  smsRoute,
  operatorLookupRoute,
  airtimeAndDataRoute,
  accountTransfersRoute,
  ondemandRoute,
  payoutRoute,
  merchantPaymentRoute,
  customerValidationRoute,
  customerAuthenticationRoute,
  customerEnrollmentRoute,
  productsAndServicesRoute,
  accountManagementRoute
];

export const getPortalRoutes = (
  checkIfUserHasPortalPrivilege: (userPortalPrivilege: PORTAL_PRIVILEGE) => boolean
): (PortalRoute | CommonRoute)[] => {
  const privilegeRoutes = portalRoutes.reduce((reducedRoutes, privateRoute) => {
    const { portalPrivilegeVerification } = privateRoute;

    if (portalPrivilegeVerification(checkIfUserHasPortalPrivilege)) {
      reducedRoutes.push(privateRoute);
    }

    return reducedRoutes;
  }, [] as PortalRoute[]);

  const redirectPortalPath =
    privilegeRoutes.map(route => route.path).find(path => path === PORTAL_PATH.DASHBOARD) ||
    privilegeRoutes[0]?.path ||
    PORTAL_PATH.DASHBOARD; // when

  const redirectRoute = redirectTo(redirectPortalPath);

  return [...privilegeRoutes, redirectRoute];
};

export const isPortalRoute = (route: any): boolean =>
  portalRoutes.some(portalRoute => route.includes(portalRoute.path));
